/**
 * Estado del saldo de créditos — criterio ÚNICO para toda la app (ADR-2026-07-30-solo-criterio-saldo-toda-app).
 *
 * Antes cada superficie decidía por su cuenta cuándo un usuario "se está quedando
 * sin": el banner de home usaba un `<= 3` fijo, que en un plan de 500 créditos no
 * significa nada, y la barra superior sólo miraba el cero. Ahora los dos avisos
 * —"se le está por acabar" y "se quedó sin"— salen de acá, y el umbral es
 * **relativo a la capacidad del plan**, así vale igual para el gratis que para
 * Ultra.
 *
 * La capacidad es `company_profiles.max_fotos_mes`. Si no se conoce (todavía no
 * cargó, o la fila no la tiene), sólo se puede afirmar el cero: sin denominador no
 * hay porcentaje que calcular y se prefiere no avisar de más.
 */

/** Porción de la capacidad del plan por debajo de la cual se avisa. */
export const UMBRAL_POR_ACABARSE = 0.05;

/**
 * Capacidad mensual por plan — el `max_fotos_mes` que tendría cada uno (ADR-2026-07-31-contador-sidebar-habla-creditos-sale).
 *
 * Es el denominador de todo lo de arriba **cuando no hay fila real que consultar**:
 * lo usa el preview de la home nueva (`/inicio`), que corre sin sesión. Cuando hay
 * datos reales manda `company_profiles.max_fotos_mes`.
 *
 * Vive acá porque estaba copiado en tres lugares (`useHomeBanners`, el shell de
 * `/inicio` y —al sumarse la sidebar— iba por el cuarto), y una capacidad distinta
 * entre pantallas mueve el umbral del 5% y hace que una avise y la otra no.
 */
export const CAPACIDAD_POR_PLAN: Record<string, number> = {
  gratis: 10,
  emprendimiento: 60,
  negocio: 250,
  enterprise1: 500,
  enterprise2: 1000,
  enterprise3: 2000,
};

export type EstadoCreditos =
  /** Saldo en cero (o negativo). */
  | 'sin'
  /** Todavía quedan, pero por debajo del umbral. */
  | 'pocos'
  /** Nada que avisar. */
  | 'ok';

/**
 * Cuántos créditos son "pocos" para un plan de `total` de capacidad. Redondea
 * hacia arriba para que el aviso exista incluso en planes chicos: con 10 de
 * capacidad, 5% da 0,5 y sin el `ceil` no avisaría nunca.
 */
export function umbralPocosCreditos(total: number | null | undefined): number | null {
  if (typeof total !== 'number' || total <= 0) return null;
  return Math.ceil(total * UMBRAL_POR_ACABARSE);
}

/**
 * Estado del saldo. `restantes` es `company_profiles.imagenes_disponibles`
 * —el saldo del plan MÁS los créditos de regalo (referidos), que es lo único
 * que el usuario puede gastar de verdad— y `total` es `max_fotos_mes`.
 *
 * Que el denominador sea sólo la capacidad del plan es a propósito: el umbral
 * del 5% mide "cuánto le queda del mes", y los regalos no agrandan el mes. El
 * efecto práctico es que quien tiene bonus deja de ver el aviso antes, que es
 * lo correcto — todavía puede generar.
 */
export function estadoDeCreditos(
  restantes: number | null | undefined,
  total: number | null | undefined,
): EstadoCreditos {
  if (typeof restantes !== 'number') return 'ok';
  if (restantes <= 0) return 'sin';
  const umbral = umbralPocosCreditos(total);
  if (umbral !== null && restantes <= umbral) return 'pocos';
  return 'ok';
}
